import LayoutPlayer from "@components/Players/LayoutPlayer";
import QueryFetchStatePage from "@components/QueryFetchStatePage";
import Divider from "@components/shared/Divider";
import TemplateCategoriesSelector from "@components/shared/TemplateCategories/TemplateCategoriesSelector";
import TemplateSearchBar from "@components/shared/TemplateCategories/TemplateSearchBar";
import { Button } from "@components/ui/button";
import { useLayouts } from "@hooks/useLayouts";
import { usePublicTemplates } from "@hooks/usePublicTemplates";
import { ScreenWithPlayingInfo } from "@hooks/useScreens";
import { ScreenPlayingOptionType } from "@hooks/useScreens/types";
import { cn } from "@utils/index";
import Link from "next/link";
import { useMemo } from "react";
import { MdAdd, MdDone } from "react-icons/md";
import { Layout, Template } from "types/index";
import { useSelectedContent } from ".";

export interface SelectCustomLayoutProps extends React.ComponentProps<"button"> {
  screenInfo: ScreenWithPlayingInfo;
  selected?: ScreenPlayingOptionType;
  onSelected: (opt: ScreenPlayingOptionType) => void;
  onPreview(
    opt: ScreenPlayingOptionType,
  ): void;
  listOnly?: boolean;
}

function SelectCustomLayout(props: SelectCustomLayoutProps) {
  return (
    <SelectCustomLayoutPresenter {...props} />
  )
}

function SelectCustomLayoutPresenter({
  screenInfo,
  selected,
  onPreview,
  listOnly = false,
  className,
  ...props
}: SelectCustomLayoutProps) {
  const { layouts: _layouts, layoutsFetchStatus, getLayouts } = useLayouts();
  const {
    templates,
    templatesFetchStatus,
    getTemplates,
    selectedCategoryId,
    setSelectedCategoryId,
    searchString,
    setSearchString,
  } = usePublicTemplates();

  const layouts = useMemo(() => {
    if (!searchString) return _layouts
    return _layouts.filter(l => l.name?.toLowerCase().includes(searchString.toLowerCase()))
  }, [_layouts, searchString])

  const getSelected = useSelectedContent()
  const _selected = getSelected(screenInfo)?.[0]

  const currentlyUsedDesign = _selected?.type === "layout" || _selected?.type === "template" ? _selected : null;

  return (
    <QueryFetchStatePage
      queryFetchStatus={layoutsFetchStatus}
      onRetry={getLayouts}
    >
      <div className="w-full gap-2 overflow-y-auto overflow-x-clip">
        {
          !listOnly && <>
            <div className="flex w-full items-center justify-center">
              <TemplateSearchBar value={searchString} onChange={setSearchString} />
            </div>
            <TemplateCategoriesSelector
              selectedCategoryId={selectedCategoryId}
              onClick={setSelectedCategoryId}
            />
            <div className="h-4"></div>
          </>
        }

        <QueryFetchStatePage
          queryFetchStatus={templatesFetchStatus}
          onRetry={getTemplates}
        >
          <div className="flex h-full w-full flex-col items-center gap-2">
            {currentlyUsedDesign && (
              <>
                {
                  currentlyUsedDesign.type === "layout" &&
                  _layoutsGrid({
                    title: "Currently Used",
                    showCreateButton: false,
                    designs: [currentlyUsedDesign].map((l) => ({
                      id: l.id,
                      height: l.height,
                      name: l.name ?? "",
                      width: l.width,
                      isSelected: selected?.id === l.id,
                      template_id: l.template_id,
                      template: { ...l, type: "layout" },
                      onClick() {
                        onPreview(l);
                        // onSelected({
                        //   id: l.id,
                        //   type: "layout",
                        // });
                      },
                      onPreview() {
                        onPreview(l);
                      },
                    })),
                  })
                }

                {
                  currentlyUsedDesign.type === "template" &&
                  _layoutsGrid({
                    designs: [currentlyUsedDesign].map((l) => ({
                      id: l.id,
                      height: l.height,
                      name: l.name ?? "",
                      width: l.width,
                      image_src: l.image_src ?? undefined,
                      template: { ...l, type: "template" },
                      isSelected: l.id === selected?.id,
                      onClick() {
                        // cloneDesign(l);
                        onPreview(l);
                      },
                      onPreview() {
                        onPreview(l);
                      },
                    })),
                    showCreateButton: false,
                    title: "Currently Used",
                  })}

                <Divider />
              </>
            )}
            {layouts.length > 0 && (
              <>
                {_layoutsGrid({
                  title: "My Designs",
                  showCreateButton: templates.length === 0,
                  designs: layouts.map((l) => ({
                    id: l.id,
                    height: l.height,
                    name: l.name ?? "",
                    width: l.width,
                    isSelected: selected?.id === l.id,
                    template_id: l.template_id,
                    template: l,
                    onClick() {
                      onPreview(
                        {
                          type: "layout",
                          ...l,
                        }
                      );
                      // onSelected({
                      //   id: l.id,
                      //   type: "layout",
                      // });
                    },
                    onPreview() {
                      onPreview(
                        {
                          type: "layout",
                          ...l,
                        }
                      );
                    },
                  })),
                })}
                <Divider />
              </>
            )}
            {templates.length > 0 && (
              <>
                {_layoutsGrid({
                  designs: templates.map((l) => ({
                    id: l.id,
                    height: l.height,
                    name: l.name ?? "",
                    width: l.width,
                    image_src: l.image_src ?? undefined,
                    template: l,
                    isSelected: l.id === selected?.id,
                    onClick() {
                      // cloneDesign(l);
                      onPreview(
                        {
                          type: "template",
                          ...l,
                        }
                      );
                    },
                    onPreview() {
                      onPreview(
                        {
                          type: "template",
                          ...l,
                        }
                      );
                    },
                  })),
                  showCreateButton: templates.length > 0,
                  title: "Browse Free Designs",
                })}
              </>
            )}
          </div>
        </QueryFetchStatePage>
      </div>
    </QueryFetchStatePage>
  );

  function _layoutsGrid({
    designs,
    showCreateButton,
    title,
  }: {
    designs: {
      id: string;
      name: string;
      width: number;
      height: number;
      template_id?: string | null;
      image_src?: string;
      onClick: () => void;
      onPreview: () => void;
      isSelected: boolean;
      template: Template | Layout;
    }[];
    title: string;
    showCreateButton?: boolean;
  }) {
    return (
      <div className="flex w-full flex-col gap-4">
        {
          !listOnly && (
            <div className="flex w-full items-center justify-between text-base font-semibold">
              {title}
              <>{showCreateButton ? <>{_createDesignButton()}</> : <></>}</>
            </div>
          )
        }

        <div className="grid w-full grid-cols-2 items-center justify-center gap-4 sm:gap-2 px-2 lg:grid-cols-3 xl:grid-cols-4">
          {designs.map((template, idx) => {
            return (
              <button
                {...props}
                className={cn("group flex w-full flex-col items-start", className)}
                key={`template_${idx}`}
                onClick={template.onClick}
              >
                <div className={cn(
                  "relative h-36 flex w-full flex-col items-center justify-center overflow-clip rounded-md bg-lightCard group-hover:outline group-hover:outline-accentColor dark:bg-darkCard dark:group-hover:outline-darkAccentColor",
                  listOnly && "h-12"
                )}>
                  <LayoutPlayer layoutInfo={template.template} muted key={template.id} />
                </div>
                <div className={cn(
                  "flex w-full items-center justify-start pt-2 sm:pt-0",
                )}>
                  {
                    !listOnly && (
                      <h1 className="w-full grow truncate text-left text-sm font-medium">
                        {template.name}
                      </h1>
                    )
                  }

                  {
                    !listOnly && (
                      <Button
                        size="icon"
                        className={cn(
                          "transition-all",
                          template.isSelected ? "scale-100" : "scale-0"
                        )}
                      >
                        <MdDone />
                      </Button>
                    )
                  }

                  {/* <Button
                    size="square"
                    variant="ghost"
                    data-tip="Preview"
                    disabled={isPreviewLoading}
                    onClick={(e) => {
                      e.stopPropagation();
                      template.onPreview();
                    }}
                  >
                    <MdRemoveRedEye />
                  </Button> */}
                </div>
                {
                  !listOnly && (
                    <div className="h-4">
                      <p className="text-xs font-normal opacity-0 transition-all group-hover:opacity-100">
                        {`${template.width}x${template.height} px`}
                      </p>
                    </div>
                  )
                }
              </button>
            );
          })}
        </div>
      </div>
    );
  }

  function _createDesignButton() {
    return (
      <Link href={"/layout"} target="_blank" rel="noopener noreferrer">
        <Button size="sm" variant="filled">
          <MdAdd />
          Create a custom Design
        </Button>
      </Link>
    );
  }
}

export default SelectCustomLayout;
